import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Heart, Target, Eye, Users, Award, Globe } from "lucide-react";
import { Link } from "react-router-dom"; // @ts-ignore - react-router-dom is not a valid module
import PublicLayout from "./PublicLayout";
import navigationRoutes, {
  useKarunaLink,
} from "@/project/karuna/utils/navigationRoutes";

const About = () => {
  const getLink = useKarunaLink();
  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="w-full bg-gradient-hero py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="mb-6 text-5xl font-bold">
            About Karuna International
          </h1>
          <p className="mx-auto max-w-3xl text-xl">
            Building compassionate global citizens through value-based education
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            <Card className="shadow-medium">
              <CardHeader>
                <Eye className="mb-4 h-12 w-12 text-primary" />
                <CardTitle className="text-2xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="leading-relaxed text-muted-foreground">
                  To create a world where compassion, kindness, and respect for
                  all living beings are fundamental values taught to every
                  child. We envision a future where young minds grow into
                  responsible global citizens committed to environmental
                  conservation and ethical living.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-medium">
              <CardHeader>
                <Target className="mb-4 h-12 w-12 text-secondary" />
                <CardTitle className="text-2xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="leading-relaxed text-muted-foreground">
                  To awaken inherent love, kindness, and compassion in children
                  and young adults towards all living beings through value-based
                  education, cultural programs, and hands-on activities that
                  promote environmental awareness and ethical behavior.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold">What We Do</h2>
            <div className="mx-auto h-1 w-24 bg-primary" />
          </div>
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3">
            <Card>
              <CardHeader>
                <Users className="mb-4 h-12 w-12 text-primary" />
                <CardTitle>Karuna Clubs</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Establish and support compassion clubs in schools across
                  India, engaging students in activities that promote kindness
                  towards animals and environmental stewardship.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Award className="mb-4 h-12 w-12 text-secondary" />
                <CardTitle>Value Education</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Develop and deliver comprehensive value education programs
                  that integrate compassion, ethics, and environmental awareness
                  into school curricula.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Globe className="mb-4 h-12 w-12 text-accent" />
                <CardTitle>Community Outreach</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Organize workshops, seminars, and events that bring together
                  educators, students, and community members to promote
                  compassionate living and sustainability.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section className="bg-background py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold">Our Impact</h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-primary" />
            <p className="mx-auto max-w-3xl text-xl text-muted-foreground">
              Over 25 years of making a difference in communities across India
            </p>
          </div>
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-gradient-card p-6 text-center shadow-medium">
              <h3 className="mb-2 text-4xl font-bold text-primary">2850+</h3>
              <p className="text-lg text-muted-foreground">
                Active Karuna Clubs
              </p>
            </div>
            <div className="rounded-lg bg-gradient-card p-6 text-center shadow-medium">
              <h3 className="mb-2 text-4xl font-bold text-secondary">
                18+ Lakh
              </h3>
              <p className="text-lg text-muted-foreground">Students Reached</p>
            </div>
            <div className="rounded-lg bg-gradient-card p-6 text-center shadow-medium">
              <h3 className="mb-2 text-4xl font-bold text-accent">15000+</h3>
              <p className="text-lg text-muted-foreground">
                Activities Conducted
              </p>
            </div>
            <div className="rounded-lg bg-gradient-card p-6 text-center shadow-medium">
              <h3 className="mb-2 text-4xl font-bold text-primary">28</h3>
              <p className="text-lg text-muted-foreground">States Covered</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold">Our Core Values</h2>
            <div className="mx-auto h-1 w-24 bg-primary" />
          </div>
          <div className="mx-auto max-w-4xl space-y-6">
            <Card>
              <CardHeader>
                <Heart className="mr-3 inline-block h-8 w-8 text-primary" />
                <CardTitle className="inline-block">Compassion</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We believe in fostering deep empathy and care for all living
                  beings, teaching children to extend kindness beyond their
                  immediate circle.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Award className="mr-3 inline-block h-8 w-8 text-secondary" />
                <CardTitle className="inline-block">Integrity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We uphold the highest standards of honesty and ethical
                  behavior in all our programs and interactions with students,
                  schools, and communities.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Globe className="mr-3 inline-block h-8 w-8 text-accent" />
                <CardTitle className="inline-block">Sustainability</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We promote environmental consciousness and sustainable
                  practices that ensure a healthier planet for future
                  generations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-accent py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-6 text-4xl font-bold">Join Our Movement</h2>
          <p className="mx-auto mb-8 max-w-2xl text-xl">
            Be part of building a more compassionate world. Whether as a school,
            volunteer, or supporter, your contribution makes a difference.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              className="border-none bg-transparent hover:bg-transparent"
              asChild
            >
              <Link to={getLink(navigationRoutes.DEMO_LOGIN)}>
                School Portal
              </Link>
            </Button>
            <Button
              className="border-none bg-transparent hover:bg-transparent"
              asChild
            >
              <Link to={getLink(navigationRoutes.CONTACT)}>Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default About;
